import { Controller } from "@hotwired/stimulus"
import { primeModalFrames } from "../lib/modal_frames"

// Global keyboard shortcuts (beyond the ⌘K palette, which has its own controller).
// Mounted on <body>; navigation URLs come in as data-shortcuts-*-value, populated
// from the layout (workspace-level always; project-level only when @project is set).
//
//   ? or ⌘/   toggle the shortcuts help modal
//   c         open the new-item modal
//   /         focus the toolbar query bar
//
//   Navigation chords — press g, then:
//     h home          y your work     d drafts        n inbox/notifications
//     p pages         c cycles        e epics         v views
//     s stickies      w wiki          i project items
//   (project-scoped targets are no-ops when not inside a project.)
//
//   On an open work item (peek panel or /browse/KEY):
//     e/s/p/a   focus title / state / priority / assignee
//     l         focus labels        d  focus the due-date control
//   Copy chords — press y, then:  (or the ⌘ combos)
//     i / ⌘.    copy the work item key (e.g. WEB-12)
//     t / ⌘⇧.   copy the title
//     u / ⌘⇧,   copy the deep-link URL
//
// Typing in inputs, textareas, selects or contenteditable is never hijacked
// (except the help modal toggle), and bare keys never override ⌘/Ctrl/Alt combos.
export default class extends Controller {
  static values = {
    home: String, items: String, yourWork: String, drafts: String,
    notifications: String, pages: String, cycles: String, epics: String,
    views: String, stickies: String, wiki: String
  }

  connect() {
    this.chord = null
    this.keydown = this.handle.bind(this)
    document.addEventListener("keydown", this.keydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
  }

  handle(e) {
    // Help modal: ⌘//Ctrl+/ works even while typing; a bare "?" must NOT — otherwise
    // (FD-449) the "?" can never be typed into a title/comment/search/Trix field
    // because this body-level handler swallows it and pops the help modal instead.
    if (((e.metaKey || e.ctrlKey) && e.key === "/") || (e.key === "?" && !this.isTyping(e))) {
      e.preventDefault()
      this.toggleHelp()
      return
    }

    // Copy combos use ⌘/Ctrl deliberately; handle them before the typing guard so
    // they still work with the title input focused, but only when an item is open.
    if ((e.metaKey || e.ctrlKey) && this.copyCombo(e)) return

    if (this.isTyping(e) || e.metaKey || e.ctrlKey || e.altKey) return

    // Two-key chords: "g" navigation, "y" copy.
    if (this.chord === "g") { this.chord = null; this.navChord(e); return }
    if (this.chord === "y") { this.chord = null; this.copyChord(e); return }
    if (e.key === "g" || e.key === "y") { this.startChord(e.key); return }

    switch (e.key) {
      case "c":
        e.preventDefault()
        this.openModal("new-item-modal")
        break
      case "/":
        e.preventDefault()
        this.focusQueryBar()
        break
      case "e": this.focusItemField(e, "title"); break
      case "s": this.focusItemField(e, "state"); break
      case "p": this.focusItemField(e, "priority"); break
      case "a": this.focusItemField(e, "assignee"); break
      case "l": this.focusItemField(e, "labels"); break
      case "d": this.focusItemField(e, "due-date"); break
    }
  }

  startChord(key) {
    this.chord = key
    setTimeout(() => { this.chord = null }, 800)
  }

  navChord(e) {
    const map = {
      h: this.hasHomeValue && this.homeValue,
      i: this.hasItemsValue && this.itemsValue,
      y: this.hasYourWorkValue && this.yourWorkValue,
      d: this.hasDraftsValue && this.draftsValue,
      n: this.hasNotificationsValue && this.notificationsValue,
      p: this.hasPagesValue && this.pagesValue,
      c: this.hasCyclesValue && this.cyclesValue,
      e: this.hasEpicsValue && this.epicsValue,
      v: this.hasViewsValue && this.viewsValue,
      s: this.hasStickiesValue && this.stickiesValue,
      w: this.hasWikiValue && this.wikiValue
    }
    const url = map[e.key]
    if (url) { e.preventDefault(); window.location.href = url }
  }

  // y-chord copy actions: i key / t title / u url.
  copyChord(e) {
    const item = this.openItem()
    if (!item) return
    if (e.key === "i") { e.preventDefault(); this.copy(item.dataset.shortcutKey) }
    else if (e.key === "t") { e.preventDefault(); this.copy(item.dataset.shortcutTitle) }
    else if (e.key === "u") { e.preventDefault(); this.copy(item.dataset.shortcutUrl) }
  }

  // ⌘. / ⌘⇧. / ⌘⇧, copy combos. Returns true if it handled the event.
  copyCombo(e) {
    const item = this.openItem()
    if (!item) return false
    if (e.key === "." && !e.shiftKey) { e.preventDefault(); this.copy(item.dataset.shortcutKey); return true }
    if (e.key === "." && e.shiftKey) { e.preventDefault(); this.copy(item.dataset.shortcutTitle); return true }
    if (e.key === "," && e.shiftKey) { e.preventDefault(); this.copy(item.dataset.shortcutUrl); return true }
    return false
  }

  copy(text) {
    if (text) navigator.clipboard?.writeText(text)
  }

  openItem() {
    return document.querySelector("[data-shortcut-item]")
  }

  toggleHelp() {
    const dialog = document.getElementById("shortcuts-modal")
    if (!dialog) return
    dialog.open ? dialog.close() : dialog.showModal()
  }

  openModal(id) {
    const dialog = document.getElementById(id)
    if (!dialog || typeof dialog.showModal !== "function") return
    // FD-501: mirror modal_controller#open — load deferred frames (the lazy new-item
    // form) since this path bypasses modal#open. No src: `c` opens a blank new item, so
    // CLM-442's create frame resets to its unprefilled src.
    primeModalFrames(dialog)
    dialog.showModal()
  }

  async focusQueryBar() {
    const focus = (input) => { input.focus(); input.select?.() }
    const frame = document.getElementById("filters_menu")
    const popover = frame?.closest("cs-popover")
    const input = document.querySelector("[data-shortcuts-search]")
    if (input && !popover?.contains(input)) return focus(input)
    // FD-501, FD-1298: the PQL box lives in the Filters popover, whose body loads on its
    // first open (frame-reveal, on cs-show). Only where the menu carries the query box:
    // open the popover, wait until it has shown and its body has loaded, then focus the box.
    const src = popover?.dataset.frameRevealSrcValue
    if (!popover || !src?.includes("pql_box=1")) return
    // The frame's `complete` ATTRIBUTE, not its property: Turbo sets the attribute when a
    // load finishes, while the property only means "not loading now", which a frame that
    // has never loaded also is.
    const shown = popover.open ? null : new Promise((resolve) => popover.addEventListener("cs-after-show", resolve, { once: true }))
    const loaded = frame.hasAttribute("complete") ? null : new Promise((resolve) => frame.addEventListener("turbo:frame-load", resolve, { once: true }))
    popover.open = true
    await Promise.all([ shown, loaded ])
    const box = popover.querySelector("[data-shortcuts-search]")
    if (box) focus(box)
  }

  focusItemField(e, field) {
    const el = document.querySelector(`[data-shortcut-field="${field}"]`)
    if (!el) return
    e.preventDefault()
    el.focus()
    // Inline-edit controls (e.g. the due-date pill) only reveal their input on
    // activation — click to open them rather than just focusing the trigger.
    if (el.hasAttribute("data-shortcut-activate")) el.click()
  }

  // FD-1353: read the element the key really went to. In a Cornerstone field (cs-input,
  // cs-textarea) the event's target is the component itself, and the <input> or <textarea>
  // it holds is inside its shadow root, so checking the target let "c", "/" and the rest
  // fire while someone typed in one.
  isTyping(e) {
    const el = e.composedPath?.()[0] ?? e.target
    if (!el) return false
    const tag = el.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" ||
      el.isContentEditable
  }
}
